import React, { useEffect, useState } from "react";
import { withRouter } from "react-router-dom";
import { Store__System } from "@talentbait/shared-components";
import { withRefluxStore } from "../../../Stores";
import AdAssistantStore from "../AdAssistantStore";
import { ActionsAdAssistant } from "../../../Actions/ActionsAdAssistant/ActionsAdAssistant";
import { AssistantLayoutWrapper } from "./AssistantLayout.styled";
import NavigationSteps from "../navigation-steps/NavigationSteps";
import { useUrlBasedStep } from "../hooks/useUrlBasedStep";
import { useSaveCampaign } from "../hooks/useSaveCampaign";
import { checkCampaignStatus } from "../utils";
import { useRealtimeAdLock } from "../hooks/useRealtimeAdLock";
import { useCleanRealtimeAdLock } from "../hooks/useCleanRealtimeAdLock";
import useInactivityTimer from "../../../hooks/use-inactivity-timer.hook";
import { useSendBeaconOnUnload } from "../../../hooks/use-send-beacon.hooks";
import { useCleanRealtimeAdLockByDate } from "../hooks/useCleanRealtimeAdLockByDate";
import { useAdUpdate } from "../hooks/useAdUpdate";
import usePrevious from "../../../hooks/usePrevious";

const AssistantLayout = ({
  // Route
  match,
  location,
  history,
  // Store
  usedAds,
  dismissedAds,
  allAdCombinations,
  currentAd,
  campaignData,
  jobTitles,
  isEditModalOpen,
  isBooked,
  userProfile,
  // Props
  children
}) => {
  const { campaignId } = match.params;
  const saveCampaign = useSaveCampaign(campaignId);
  const { findNextAvailableAd } = useAdUpdate(currentAd, allAdCombinations, userProfile);

  const [isLoadingData, setIsLoadingData] = useState(false);
  const { stepText, stepName } = useUrlBasedStep({ location });
  const isEditingAd = isEditModalOpen && Object.keys(currentAd).length > 0;
  const previousIsEditModalOpen = usePrevious(isEditModalOpen);
  const selectedAds = [...usedAds, ...dismissedAds];

  // Real-time ad lock
  useRealtimeAdLock({ selectedAds, campaignData });
  useCleanRealtimeAdLock({ selectedAds, userProfile, isEditModalOpen });
  useCleanRealtimeAdLockByDate({ selectedAds });

  // Inactivity timer
  useInactivityTimer(30, () => {
    if (isEditingAd) {
      window.location.reload();
    }
  });

  // Send beacon on unload
  useSendBeaconOnUnload({
    data: {
      adId: currentAd.id,
      userId: userProfile.userId
    },
    url: "/api/v2/ads/unlock",
    enabled: isEditingAd
  });

  const isThankYouPage = location.pathname.includes("/thank-you");

  useEffect(() => {
    return () => {
      ActionsAdAssistant.resetAssistantData();
    };
  }, []);

  // Redirect to overview page if the campaign is booked
  useEffect(() => {
    if (
      isBooked &&
      !location.pathname.includes("/overview") &&
      !location.pathname.includes("/thank-you")
    ) {
      history.push(`/assistant/overview/${campaignId}`);
    }
  }, [location, isBooked, history, campaignId]);

  // Get ad combinations and assistant campaign
  useEffect(() => {
    // Request new campaign data if the campaign id is present and the ad combinations are not present
    const requestNewCampaignData = campaignId && allAdCombinations.length === 0 && !isLoadingData;
    if (requestNewCampaignData) {
      // Avoid multiple requests
      setIsLoadingData(true);

      ActionsAdAssistant.getAdCombinations(campaignId);
      ActionsAdAssistant.getAssistantCampaign(campaignId);
    }

    // Set the current ad if the ad combinations are present and the current ad is not present
    const shouldSetNewCurrentAd =
      allAdCombinations.length > 0 && Object.keys(currentAd).length === 0;
    if (shouldSetNewCurrentAd) {
      const firstUnusedAd = allAdCombinations.find(
        (combination) => combination.status === "unused" || combination.status === "temp"
      );

      ActionsAdAssistant.setCurrentAd(firstUnusedAd);
    }
  }, [campaignId, allAdCombinations, currentAd, campaignData, isLoadingData]);

  // Get job titles if needed and ad versions
  useEffect(() => {
    if (!campaignData || Object.keys(campaignData).length === 0) return;
    if (!currentAd || Object.keys(currentAd).length === 0) return;

    if (campaignData?.jobCategoryId && jobTitles?.length === 0) {
      ActionsAdAssistant.getJobTitlesByCategoryId(campaignData?.jobCategoryId);
    }
  }, [campaignData, currentAd, jobTitles]);

  // Found next available ad if last edited was not a temp ad
  useEffect(() => {
    if (!isEditingAd && previousIsEditModalOpen) {
      if (currentAd.status !== "temp") {
        findNextAvailableAd(allAdCombinations);
      }
    }
  }, [currentAd, allAdCombinations, findNextAvailableAd, previousIsEditModalOpen, isEditingAd]);

  // Get current ad versions
  useEffect(() => {
    if (currentAd?.id) {
      ActionsAdAssistant.emptyAdVersions();
      ActionsAdAssistant.getAdVersions(currentAd.id);
    }
  }, [currentAd]);

  const handleCloseAssistant = async () => {
    // Redirect to dashboard if the campaign data is not present
    if (!campaignData || Object.keys(campaignData).length === 0) {
      history.push(`/assistant-dashboard`);
      return;
    }

    // Save step user is on
    const newStatus = checkCampaignStatus(campaignData);
    await saveCampaign({ ...campaignData, step: stepName, status: newStatus });
    history.push(`/assistant-dashboard`);
  };

  return (
    <AssistantLayoutWrapper>
      {!isEditModalOpen && !isThankYouPage && (
        <NavigationSteps step={stepText} onClose={handleCloseAssistant} hideSteps={isBooked} />
      )}
      {children}
    </AssistantLayoutWrapper>
  );
};

export default withRouter(
  withRefluxStore(
    AssistantLayout,
    [AdAssistantStore, Store__System],
    [
      "allAdCombinations",
      "currentAd",
      "campaignData",
      "jobTitles",
      "jobTitle",
      "isEditModalOpen",
      "isBooked",
      "usedAds",
      "dismissedAds",
      "userProfile"
    ]
  )
);
